import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Database, CheckCircle2, AlertTriangle, Loader2, Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = "#4338CA";
const MIN_KEY = 32;

function Step({ n, title, state, children }) {
  const ring = state === "done" ? "bg-emerald-600" : state === "active" ? "" : "bg-slate-300";
  return (
    <section className={`rounded-2xl border p-5 ${state === "active" ? "border-indigo-300 bg-white shadow-md" : "border-slate-200 bg-white/60"}`}>
      <div className="flex items-start gap-3">
        <span
          className={`w-7 h-7 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0 ${ring}`}
          style={state === "active" ? { backgroundColor: ACCENT } : undefined}
        >
          {state === "done" ? <CheckCircle2 className="w-4 h-4" /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={`font-bold ${state === "todo" ? "text-slate-400" : "text-slate-900"}`}>{title}</h2>
          <div className={`mt-2 text-sm leading-relaxed ${state === "todo" ? "text-slate-400" : "text-slate-600"}`}>
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DbKeyRotation() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupChecked, setBackupChecked] = useState(false);
  const [currentKey, setCurrentKey] = useState("");
  const [newKey, setNewKey] = useState("");
  const [rotating, setRotating] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/security/key-status`, { withCredentials: true });
      setStatus(data);
    } catch {
      // The procedure remains usable without it; only the state indicators
      // are unavailable.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // The variable is changed outside Core, so the state is re-read when the tab
  // regains focus rather than requiring a manual reload.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const onRotate = async () => {
    setRotating(true);
    setConfirm(false);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/security/rotate-key`,
        { current_key: currentKey, new_key: newKey },
        { withCredentials: true }
      );
      // The endpoint answers 200 for a partial failure as well as for success,
      // and in that case the stored key is deliberately left unchanged. Reading
      // only the HTTP status reported a failed rotation as a completed one.
      const ok = data.status === "success";
      setResult({ ok, ...data });
      if (ok) {
        setCurrentKey("");
        toast.success("Rotazione completata");
      } else {
        toast.error("Rotazione incompleta");
      }
      await load();
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.detail || "Rotazione non riuscita." });
      toast.error(err.response?.data?.detail || "Rotazione non riuscita");
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  // Three distinct states, previously collapsed into two: an installation that
  // has never rotated showed the same confirmation as one where the variable
  // had been correctly updated.
  const recorded = status?.db_key?.rotation_recorded === true;
  const pending = status?.db_key?.needs_railway_update === true;
  const rotated = recorded || result?.ok === true;

  // The counts are nested inside report. Read from the top level they were
  // undefined, and the panel printed a sentence with no numbers in it.
  const counts = result?.report;
  const failures = counts?.failed?.length || 0;

  return (
    <div className="min-h-screen">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/settings" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alle impostazioni
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: ACCENT }}>
            <Database className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Rotazione della chiave del database</h1>
        </div>

        <div className="text-sm text-slate-600 leading-relaxed mb-6 space-y-2">
          <p>
            Nomi utente, indirizzi email, titoli e descrizioni dei progetti, aggiornamenti,
            trasferte e clienti sono cifrati nel database. La procedura sostituisce la chiave: ogni
            campo viene decifrato con la chiave in uso e riscritto con quella nuova.
          </p>
          <p>
            La riscrittura avviene sui documenti esistenti e non dispone di un comando di
            annullamento. Un'interruzione lascia una parte dei documenti sulla chiave nuova e una
            parte su quella precedente; ogni documento registra la chiave con cui è stato cifrato,
            quindi una nuova esecuzione riprende dai documenti rimasti indietro.
          </p>
        </div>

        <div className="space-y-4">
          <Step n={1} title="Backup del database" state={backupChecked ? "done" : "active"}>
            <p className="mb-3">
              La procedura crea un backup prima di iniziare e si interrompe se la creazione
              fallisce: se lo storage di backup non è configurato, la rotazione non viene eseguita.
              Il backup è l'unico ripristino disponibile se la riscrittura termina in uno stato non
              recuperabile.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={backupChecked}
                onChange={(e) => setBackupChecked(e.target.checked)}
                data-testid="db-key-backup-check"
                className="mt-1"
              />
              <span className="text-sm">Backup recente verificato.</span>
            </label>
          </Step>

          <Step n={2} title="Esecuzione della rotazione" state={rotated ? "done" : backupChecked ? "active" : "todo"}>
            <p className="mb-2">
              La chiave in uso viene richiesta come verifica: se non corrisponde, la procedura si
              interrompe prima di modificare qualsiasi dato.
            </p>
            <p className="mb-2">
              La chiave nuova deve avere almeno {MIN_KEY} caratteri casuali. Non viene più mostrata
              dopo l'esecuzione ed è necessaria alla fase 3 e per qualsiasi ripristino successivo.
            </p>
            <p className="mb-3">
              Durante l'esecuzione la pagina resta in attesa. Un riavvio del container in questa
              fase interrompe la riscrittura.
            </p>
            {!rotated && (
              <div className="space-y-2">
                <Input
                  type="password"
                  value={currentKey}
                  onChange={(e) => setCurrentKey(e.target.value)}
                  placeholder="Chiave in uso"
                  disabled={!backupChecked}
                  className="border-slate-200 font-mono text-xs"
                />
                <Input
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder={`Chiave nuova (minimo ${MIN_KEY} caratteri)`}
                  disabled={!backupChecked}
                  className="border-slate-200 font-mono text-xs"
                />
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setConfirm(true)}
                    disabled={!backupChecked || !currentKey || newKey.length < MIN_KEY || rotating}
                    className="text-white"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {rotating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Riscrittura in corso</> : <><Play className="w-4 h-4 mr-1.5" />Avvia la rotazione</>}
                  </Button>
                  <span className="text-xs text-slate-400 tabular-nums">{newKey.length} caratteri</span>
                </div>
              </div>
            )}

            {result && (
              <div className={`mt-3 rounded-xl border p-3 text-sm ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"}`}>
                {counts ? (
                  <p>
                    Documenti riscritti: <strong className="tabular-nums">{counts.rotated}</strong>.
                    Già sulla chiave nuova: <span className="tabular-nums">{counts.skipped}</span>.
                    Totale esaminati: <span className="tabular-nums">{counts.total}</span>.
                  </p>
                ) : (
                  <p>{result.message}</p>
                )}
                {failures > 0 && (
                  <p className="mt-2">
                    {failures} documenti non sono stati riscritti. La chiave in uso non è stata
                    sostituita e resta quella precedente: il database è leggibile e non va
                    aggiornata alcuna variabile. Una nuova esecuzione salta i documenti già
                    convertiti.
                  </p>
                )}
              </div>
            )}
          </Step>

          <Step n={3} title="Aggiornamento della variabile su Railway" state={!rotated ? "todo" : pending ? "active" : "done"}>
            <p className="mb-3">
              La chiave è letta dalla variabile d'ambiente <code className="px-1 bg-slate-100 rounded">ENCRYPTION_KEY</code>.
              Dopo la rotazione Core utilizza la chiave nuova mantenuta in memoria, mentre la
              variabile conserva ancora quella precedente: al primo riavvio il processo ripartirebbe
              con la chiave vecchia e non sarebbe in grado di decifrare i dati appena riscritti.
            </p>
            <ol className="list-decimal list-inside space-y-1 mb-3">
              <li>Railway, progetto AMMO Core, ambiente corrispondente all'installazione in uso.</li>
              <li>Servizio <code className="px-1 bg-slate-100 rounded">AMMO-Core-1.02</code>, scheda Variables.</li>
              <li>Sostituire il valore di <code className="px-1 bg-slate-100 rounded">ENCRYPTION_KEY</code> con la chiave nuova.</li>
              <li>Salvare e attendere il completamento del deploy.</li>
            </ol>
            {!rotated ? (
              <p className="text-sm text-slate-400">Fase non ancora raggiunta.</p>
            ) : pending ? (
              <p className="text-sm font-semibold text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                Aggiornamento in sospeso: la variabile letta all'avvio non corrisponde alla chiave
                registrata dall'ultima rotazione.
              </p>
            ) : (
              <p className="text-sm font-semibold text-emerald-700 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                La variabile letta all'avvio corrisponde alla chiave registrata.
              </p>
            )}
          </Step>

          <Step n={4} title="Verifica dopo il riavvio" state={rotated && !pending ? "active" : "todo"}>
            <p className="mb-3">
              Le verifiche vanno eseguite dopo il completamento del deploy della fase 3. Prima di
              quel momento Core utilizza la chiave in memoria e qualunque controllo darebbe esito
              positivo.
            </p>
            <ul className="space-y-2">
              <li>
                <strong>Accesso.</strong> Disconnessione e nuovo accesso. Un errore di credenziali
                con la password corretta indica che l'account non viene trovato: l'indirizzo email
                è cercato tramite un hash calcolato con la chiave.
              </li>
              <li>
                <strong>Impostazioni, sezione Utenti.</strong> Nome e indirizzo di ciascun account
                devono essere leggibili. Un campo vuoto al posto di un nome indica un documento non
                convertito.
              </li>
              <li>
                <strong>Un progetto con i relativi aggiornamenti.</strong> Titolo, descrizione e
                testo devono comparire per esteso.
              </li>
              <li>
                <strong>Una trasferta con il cliente associato.</strong>
              </li>
              <li>
                <strong>Notifiche.</strong> Le voci riportano titolo del progetto e nome
                dell'autore, entrambi cifrati.
              </li>
            </ul>
            <h3 className="font-semibold text-slate-800 mt-4 mb-1">In caso di esito negativo</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>
                Verificare che la variabile contenga esattamente la chiave nuova, senza spazi
                iniziali o finali, e che il deploy sia terminato.
              </li>
              <li>
                Se una parte dei dati è leggibile e un'altra no, la riscrittura è incompleta:
                ripetere la fase 2 indicando la chiave precedente come chiave in uso.
              </li>
              <li>
                Se nessun dato è leggibile, ripristinare la chiave precedente nella variabile. I
                dati non sono danneggiati: sono cifrati con una chiave diversa da quella in uso.
              </li>
              <li>
                Se nessuna delle azioni precedenti è risolutiva, ripristinare il backup della
                fase 1.
              </li>
            </ol>
          </Step>
        </div>
      </main>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avviare la rotazione della chiave del database?</AlertDialogTitle>
            <AlertDialogDescription>
              Ogni campo cifrato viene riscritto al suo posto. L'operazione non dispone di un
              comando di annullamento e richiede l'aggiornamento della variabile su Railway subito
              dopo il completamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); onRotate(); }} className="text-white" style={{ backgroundColor: ACCENT }}>
              Avvia
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
